import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatSlotTime, formatOrdinalDate } from "../lib/timeFormat";
import { CheckCircle2, MapPin, Ban, Download } from "lucide-react";

export default function OrderTicketsPage() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/order/${orderId}/tickets`);
        setData(r.data);
      } catch { setErr("Order not found"); }
    })();
  }, [orderId]);

  if (err) return <Wrap><div className="text-center text-[#fca5a5] py-20" data-testid="order-tickets-error">{err}</div></Wrap>;
  if (!data) return <Wrap><div className="text-center text-[#8a8a8a] py-20">Loading…</div></Wrap>;

  if (data.cancelled) return (
    <Wrap>
      <div className="max-w-md mx-auto p-5" data-testid="order-tickets-cancelled">
        <Brand />
        <div className="st-card p-8 text-center st-fade-up">
          <div className="w-14 h-14 rounded-full bg-[#2a0e0e] border border-[#dc2626]/40 flex items-center justify-center mx-auto mb-4">
            <Ban size={26} className="text-[#fca5a5]" />
          </div>
          <h1 className="text-xl font-bold">Tickets no longer valid</h1>
          <p className="text-sm text-[#bbb] mt-2">
            This order has been {data.status === "refunded" ? "refunded" : "cancelled"} and the tickets can no longer be used for entry.
          </p>
          {data.order_number && <p className="text-xs text-[#8a8a8a] mt-3">Order #{data.order_number}</p>}
          <p className="text-xs text-[#8a8a8a] mt-4">If you believe this is a mistake, please contact {data.attraction?.name || "the organiser"}.</p>
        </div>
      </div>
    </Wrap>
  );

  const { attraction, order_number, tickets = [] } = data;
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/order/${orderId}/tickets/pdf`;

  return (
    <Wrap>
      <div className="max-w-md mx-auto p-5" data-testid="order-tickets-page">
        <Brand />
        <div className="text-center mb-6">
          <div className="text-lg font-bold">{attraction.name}</div>
          <p className="text-xs text-[#8a8a8a] mt-1">
            Order <span className="st-mono text-[#dc2626]">#{order_number}</span> · {tickets.length} ticket{tickets.length === 1 ? "" : "s"}
          </p>
          {tickets.length > 0 && (
            <a
              href={pdfUrl}
              className="st-btn st-btn-ghost inline-flex items-center gap-2 mt-4 text-sm"
              data-testid="download-tickets-pdf-btn"
              rel="noreferrer"
            >
              <Download size={14} /> Download all tickets (PDF)
            </a>
          )}
        </div>

        {tickets.length === 0 && (
          <div className="st-card p-8 text-center text-sm text-[#8a8a8a]" data-testid="order-tickets-empty">
            No valid tickets found for this order.
          </div>
        )}

        <div className="space-y-6">
          {tickets.map((t, i) => (
            <TicketCard key={t.attendee.id} t={t} attraction={attraction} index={i} total={tickets.length} />
          ))}
        </div>

        <p className="text-center text-xs text-[#7a7a7a] mt-8">Show each QR at the door. Don&apos;t lose your soul.</p>
      </div>
    </Wrap>
  );
}

function TicketCard({ t, attraction, index, total }) {
  const { attendee, event_date, time_slot, qr_png_data_uri } = t;
  return (
    <div className="st-card overflow-hidden st-fade-up" data-testid={`order-ticket-${attendee.id}`}>
      <div className="px-6 py-4 border-b border-[#1a1a1a] bg-[#0c0c0c] flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#dc2626]">Admit one</div>
          <div className="text-xl font-bold mt-1">{attraction.name}</div>
          <div className="text-xs text-[#8a8a8a] flex items-center gap-1 mt-1"><MapPin size={12} />{attraction.location}</div>
        </div>
        <div className="text-xs text-[#7a7a7a]">{index + 1}/{total}</div>
      </div>

      <div className="px-6 py-5 flex justify-center">
        <div className="p-4 rounded-2xl bg-white shadow-[0_0_40px_rgba(255,255,255,0.08)]" data-testid={`qr-container-${attendee.id}`}>
          <img src={qr_png_data_uri} alt="Ticket QR code" className="block w-[240px] h-[240px] max-w-full" />
        </div>
      </div>

      <div className="px-6 pb-5">
        <Detail label="Attendee" value={attendee.name} />
        <Detail label="Ticket" value={attendee.ticket_type_name} />
        <Detail label="Date" value={formatOrdinalDate(event_date.date)} />
        <Detail label="Time" value={time_slot ? formatSlotTime(time_slot.start_time, time_slot.end_time, attraction, "slot") : formatSlotTime(event_date.open_time, event_date.close_time, attraction, "openHours")} />
      </div>

      {attendee.checked_in && (
        <div className="bg-[#0c1d12] border-t border-[#1b3a25] px-6 py-3 flex items-center gap-2 text-[#86efac] text-sm">
          <CheckCircle2 size={16} /> Checked in {attendee.checked_in_at?.slice(11, 16)}
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div className="flex justify-between py-2 border-b border-[#161616] last:border-b-0">
      <span className="text-xs text-[#7a7a7a] uppercase tracking-wider">{label}</span>
      <span className={`text-sm font-semibold ${mono ? "st-mono text-[#dc2626]" : ""}`}>{value}</span>
    </div>
  );
}

function Brand() {
  return (
    <div className="text-center mb-6">
      <div className="st-brand text-3xl text-white">SCARE<span className="text-[#dc2626]">TICKETS</span></div>
    </div>
  );
}

function Wrap({ children }) {
  return <div className="min-h-screen st-grain bg-[#0a0a0a] py-10">{children}</div>;
}
